// app/src/components/admin/table/TableFilterToolbar.tsx
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
    FilterPanelTrigger,
    GridFilterListIcon,
    QuickFilter,
    QuickFilterClear,
    QuickFilterControl,
    QuickFilterTrigger,
    Toolbar,
    ToolbarButton,
} from "@mui/x-data-grid-pro";
import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";

/**
 * Custom toolbar that handles filtering.
 *
 * The Filters button stays on MUI's own `FilterPanelTrigger`/`ToolbarButton`
 * (out of scope for the search-pattern migration). The quick-filter search
 * box is rendered with shadcn `Input` + lucide icons via the headless
 * `QuickFilterControl`/`QuickFilterClear`/`QuickFilterTrigger` render props;
 * the underlying DataGridPro quick-filter wiring (value/onChange/expanded
 * state, the `q`/`sort_by: "quick"` filter contract consumed by
 * `useFilterModel`) is untouched.
 */
export const TableFilterToolbar = () => {
    const [expanded, setExpanded] = useState(true);
    const theme = useTheme();
    const isListView = useMediaQuery(theme.breakpoints.down("md"));

    useEffect(() => {
        // On mobile, the quick filter is not expanded by default
        if (isListView) {
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    }, [isListView]);

    return (
        <Toolbar style={{ borderBottom: "none" }}>
            {/* Filters Button */}
            <FilterPanelTrigger render={<ToolbarButton />}>
                <GridFilterListIcon fontSize="small" color="primary" />
            </FilterPanelTrigger>
            <Box flexGrow={1} />

            {/* Quick Filter */}
            <QuickFilter
                expanded={expanded}
                onExpandedChange={(expandedChanged) => {
                    if (isListView) {
                        setExpanded(expandedChanged);
                    }
                }}
                style={{
                    display: "grid",
                    alignItems: "center",
                    marginLeft: "auto",
                }}
            >
                <QuickFilterTrigger
                    render={(triggerProps) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { ref, ...restTriggerProps } = triggerProps;
                        return (
                            <ToolbarButton
                                {...restTriggerProps}
                                disabled={expanded}
                                style={{
                                    gridArea: "1 / 1",
                                    width: "min-content",
                                    height: "min-content",
                                    zIndex: 1,
                                    opacity: expanded ? 0 : 1,
                                    pointerEvents: expanded ? "none" : "auto",
                                    transition: theme.transitions.create([
                                        "opacity",
                                    ]),
                                }}
                            >
                                <Search size={16} />
                            </ToolbarButton>
                        );
                    }}
                />
                <QuickFilterControl
                    render={(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        { ref, slotProps, size, ...controlProps },
                        state
                    ) => (
                        <div
                            className="relative"
                            style={{
                                gridArea: "1 / 1",
                                overflow: "clip",
                                // Padding gives the shadcn Input's focus-visible
                                // ring (a box-shadow that renders outside the
                                // input's border box) room to render before it
                                // hits the clip boundary; absolutely-positioned
                                // children measure from the padding edge, so
                                // the icon/clear-button positions are unaffected.
                                padding: 3,
                                width: expanded ? (isListView ? 200 : 260) : 36,
                                opacity: expanded ? 1 : 0,
                                transition: theme.transitions.create([
                                    "width",
                                    "opacity",
                                ]),
                            }}
                        >
                            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
                            <Input
                                {...controlProps}
                                {...slotProps?.htmlInput}
                                ref={ref}
                                placeholder="Search..."
                                className="h-8 pr-8 pl-8 text-sm"
                            />
                            {state.value ? (
                                <QuickFilterClear
                                    render={(clearProps) => {
                                        const {
                                            ref: clearRef,
                                            ...restClearProps
                                        } = clearProps;
                                        return (
                                            <button
                                                type="button"
                                                ref={clearRef}
                                                {...restClearProps}
                                                aria-label="Clear search"
                                                className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 transition-colors"
                                            >
                                                <X className="size-3.5" />
                                            </button>
                                        );
                                    }}
                                />
                            ) : null}
                        </div>
                    )}
                />
            </QuickFilter>
        </Toolbar>
    );
};
